/**
 * Navigation Menu Configuration
 * Centralized navigation structure for BE Study Automation System
 */

import { 
  LayoutDashboard, 
  Users, 
  Settings,
  FileText,
  FolderOpen,
  MessageSquare,
  Palette,
  FilePlus,
  ClipboardCheck,
  Sliders,
} from 'lucide-react';
import { ROUTES } from './routes';
import { USER_ROLES } from './beStudyMockData';

/**
 * Admin Navigation Items
 */
export const ADMIN_NAVIGATION = [
  {
    id: 'admin-dashboard',
    name: 'Admin Dashboard',
    path: ROUTES.ADMIN_DASHBOARD,
    icon: LayoutDashboard,
    roles: [USER_ROLES.ADMIN],
    children: null,
  },
  {
    id: 'templates',
    name: 'Templates',
    path: ROUTES.TEMPLATES,
    icon: FolderOpen,
    roles: [USER_ROLES.ADMIN],
    children: null,
  },
  {
    id: 'documents',
    name: 'Documents',
    path: ROUTES.DOCUMENTS,
    icon: FileText,
    roles: [USER_ROLES.ADMIN],
    children: null,
  },
  {
    id: 'user-management',
    name: 'User Management',
    path: ROUTES.USER_MANAGEMENT,
    icon: Users,
    roles: [USER_ROLES.ADMIN],
    children: null,
  },
  {
    id: 'prompts',
    name: 'Prompt Management',
    path: ROUTES.PROMPTS,
    icon: MessageSquare,
    roles: [USER_ROLES.ADMIN],
    children: null,
  },
  {
    id: 'config',
    name: 'Configuration',
    path: ROUTES.CONFIG,
    icon: Sliders,
    roles: [USER_ROLES.ADMIN],
    children: null,
  },
  {
    id: 'settings',
    name: 'Settings',
    path: ROUTES.SETTINGS,
    icon: Settings,
    roles: [USER_ROLES.ADMIN],
    children: null,
  },
];

/**
 * User Navigation Items
 */
export const USER_NAVIGATION = [
  {
    id: 'user-dashboard',
    name: 'Dashboard',
    path: ROUTES.USER_DASHBOARD,
    icon: LayoutDashboard,
    roles: [USER_ROLES.USER],
    children: null,
  },
  {
    id: 'documents',
    name: 'My Documents',
    path: ROUTES.DOCUMENTS,
    icon: FileText,
    roles: [USER_ROLES.USER],
    children: null,
  },
  {
    id: 'create-document',
    name: 'Create Document',
    path: ROUTES.DOCUMENT_CREATE,
    icon: FilePlus,
    roles: [USER_ROLES.USER],
    children: null,
  },
];

/**
 * Reviewer Navigation Items
 */
export const REVIEWER_NAVIGATION = [
  {
    id: 'reviewer-dashboard',
    name: 'Review Dashboard',
    path: ROUTES.REVIEWER_DASHBOARD,
    icon: ClipboardCheck,
    roles: [USER_ROLES.REVIEWER],
    children: null,
  },
  {
    id: 'documents',
    name: 'Documents to Review',
    path: ROUTES.DOCUMENTS,
    icon: FileText,
    roles: [USER_ROLES.REVIEWER],
    children: null,
  },
];

/**
 * Combined Navigation Items (for backward compatibility)
 * Will be filtered based on role
 */
export const NAVIGATION_ITEMS = [
  // Admin items
  ...ADMIN_NAVIGATION,
  // User items (excluding duplicates)
  {
    id: 'user-dashboard',
    name: 'Dashboard',
    path: ROUTES.USER_DASHBOARD,
    icon: LayoutDashboard,
    roles: [USER_ROLES.USER],
    children: null,
  },
  // Reviewer items (excluding duplicates)
  {
    id: 'reviewer-dashboard',
    name: 'Review Dashboard',
    path: ROUTES.REVIEWER_DASHBOARD,
    icon: ClipboardCheck,
    roles: [USER_ROLES.REVIEWER],
    children: null,
  },
  // Common items
  {
    id: 'settings',
    name: 'Settings',
    path: ROUTES.SETTINGS,
    icon: Palette,
    roles: [USER_ROLES.USER, USER_ROLES.REVIEWER],
    children: null,
  },
];

/**
 * Get navigation items based on user role
 * @param {string} role - User role (ADMIN, USER, REVIEWER)
 * @returns {Array} Navigation items for the role
 */
export const getNavigationByRole = (role) => {
  switch (role) {
    case USER_ROLES.ADMIN:
      return ADMIN_NAVIGATION;
    case USER_ROLES.USER:
      return USER_NAVIGATION;
    case USER_ROLES.REVIEWER:
      return REVIEWER_NAVIGATION;
    default:
      return [];
  }
};

/**
 * Filter navigation items based on user role
 * @param {Array} items - Navigation items
 * @param {string} role - User role
 * @param {boolean} isAdmin - Whether user is admin
 * @returns {Array} Filtered navigation items
 */
export const filterNavigationByRole = (items, role, isAdmin = false) => {
  if (isAdmin || role === USER_ROLES.ADMIN) {
    return ADMIN_NAVIGATION;
  }
  
  if (role === USER_ROLES.REVIEWER) {
    return REVIEWER_NAVIGATION;
  }
  
  if (role === USER_ROLES.USER) {
    return USER_NAVIGATION;
  }
  
  // Fallback to filtering from provided items
  return items.filter((item) => {
    if (!item.roles) return true;
    return item.roles.includes(role);
  });
};

/**
 * Get navigation item by ID
 * @param {string} id - Navigation item ID
 * @returns {object|undefined} Navigation item
 */
export const getNavigationItemById = (id) => {
  const allItems = [...ADMIN_NAVIGATION, ...USER_NAVIGATION, ...REVIEWER_NAVIGATION];
  return allItems.find(item => item.id === id);
};

/**
 * Get navigation item by path
 * @param {string} path - Route path
 * @returns {object|undefined} Navigation item
 */
export const getNavigationItemByPath = (path) => {
  const allItems = [...ADMIN_NAVIGATION, ...USER_NAVIGATION, ...REVIEWER_NAVIGATION];
  return allItems.find(item => item.path === path);
};

/**
 * Check if a path is active (including child paths)
 * @param {string} currentPath - Current route path
 * @param {string} itemPath - Navigation item path
 * @returns {boolean} Whether the path is active
 */
export const isPathActive = (currentPath, itemPath) => {
  if (currentPath === itemPath) return true;
  
  // Check for child paths (e.g., /documents/123/editor matches /documents)
  if (itemPath !== '/' && currentPath.startsWith(itemPath)) {
    return true;
  }
  
  return false;
};
